import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import {
  SentEmailAttachmentModel,
  SentEmailDetailModel,
  SentEmailEventModel,
  SentEmailModel,
  SentEmailRecipientKind,
  SentEmailRecipientModel,
} from '../../../api/models/SentEmailModel';
import { SentEmailsService } from '../../../api/sent-emails.service';
import { GT2PageAbstract } from '../../../content/abstract/GT2PageAbstract';
import { SentEmailPanelService } from '../../../services/sent-email-panel.service';
import { locale as english } from '../i18n/en-CA';
import { locale as french } from '../i18n/fr-CA';
import { SentEmailStatusComponent } from '../sent-email-status/sent-email-status.component';
import { SENT_EMAIL_DATE_FORMAT, SentEmailTools } from '../sent-email-tools';

type SentEmailDetailError = 'generic' | 'not_found' | null;

interface RecipientGroup {
  kind: SentEmailRecipientKind;
  labelKey: string;
  recipients: SentEmailRecipientModel[];
}

/**
 * Right side panel « Détail du courriel » (historique des courriels,
 * MODULE_EMAIL_HISTORY) — read-only. Registered in layout-1 (`sentEmailPanel`).
 *
 * The HTML of the email is NEVER injected in the application DOM: it is
 * rendered by an isolated `<iframe sandbox>` (no allow-scripts, no
 * allow-same-origin) fed through `srcdoc`.
 */
@Component({
  selector: 'app-sent-email-detail',
  templateUrl: './sent-email-detail.component.html',
  styleUrls: ['./sent-email-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    SentEmailStatusComponent,
  ],
})
export class SentEmailDetailComponent
  extends GT2PageAbstract
  implements OnInit, OnDestroy
{
  /**
   * Injected in the `<head>` of the isolated document: links open in a new
   * tab, and scripts, plugins, frames and forms are refused a second time by
   * a CSP (defence in depth — the sandbox already forbids scripts).
   */
  private static readonly FRAME_HEAD =
    "<meta http-equiv=\"Content-Security-Policy\" content=\"script-src 'none'; object-src 'none'; frame-src 'none'; form-action 'none'\">" +
    '<base target="_blank">';

  private static readonly PROBLEM_EVENTS = ['failed', 'rejected', 'complained'];
  private static readonly SUCCESS_EVENTS = ['delivered', 'opened', 'clicked'];

  readonly dateFormat = SENT_EMAIL_DATE_FORMAT;

  uuid: string | null = null;
  /** List row shown while the detail loads, then replaced by the detail. */
  email: SentEmailModel | SentEmailDetailModel | null = null;
  detail: SentEmailDetailModel | null = null;
  override loading = false;
  errorState: SentEmailDetailError = null;

  recipientGroups: RecipientGroup[] = [];
  events: SentEmailEventModel[] = [];
  frameDocument: SafeHtml | null = null;
  textBody: string | null = null;
  downloadingAttachmentUuid: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private panelService: SentEmailPanelService,
    private sentEmailsService: SentEmailsService,
    private translationLoader: FuseTranslationLoaderService,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private logger: NGXLogger,
    public translate: TranslateService,
    public dialog: MatDialog,
    public router: Router,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);

    const data = this.panelService.getData();
    this.uuid = data?.uuid ?? null;
    this.email = data?.summary ?? null;
    this.buildRecipientGroups();
  }

  ngOnInit(): void {
    this.loadDetail();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public close(): void {
    this.panelService.emitResult(false);
  }

  public loadDetail(): void {
    if (!this.uuid) {
      this.errorState = 'not_found';
      return;
    }

    this.loading = true;
    this.errorState = null;

    this.sentEmailsService
      .getSentEmail(this.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { data: SentEmailDetailModel }) => {
          this.loading = false;
          this.detail = response?.data ?? null;
          if (!this.detail) {
            this.errorState = 'not_found';
            return;
          }
          this.email = this.detail;
          this.buildRecipientGroups();
          this.buildEvents();
          this.buildContent();
        },
        error: (error: any) => {
          this.logger.error(
            'SentEmailDetailComponent.loadDetail() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.loading = false;

          // Module removed meanwhile: nothing to show, close silently.
          if (SentEmailTools.isModuleMissingError(error)) {
            this.close();
            return;
          }

          if (error?.status === 404) {
            this.errorState = 'not_found';
            return;
          }

          this.errorState = 'generic';
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' SEM002',
            this.router,
          );
        },
      });
  }

  public onDownload(attachment: SentEmailAttachmentModel): void {
    if (
      !this.uuid ||
      !attachment?.is_downloadable ||
      this.downloadingAttachmentUuid
    ) {
      return;
    }

    // Opened inside the click itself: a tab opened after the HTTP answer is
    // treated as a pop-up by the browsers and blocked.
    const tab = window.open('', '_blank');
    this.downloadingAttachmentUuid = attachment.uuid;

    this.sentEmailsService
      .getAttachmentLink(this.uuid, attachment.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.downloadingAttachmentUuid = null;
          const url = response?.data?.url;
          if (!url) {
            tab?.close();
            this.toastr.warning(
              this.translate.instant(
                'SENT_EMAILS.DETAIL.ATTACHMENT_NOT_AVAILABLE',
              ),
            );
            return;
          }
          if (tab) {
            tab.location.href = url;
            return;
          }
          this.showBlockedTabToast(url);
        },
        error: (error: any) => {
          this.logger.error(
            'SentEmailDetailComponent.onDownload() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.downloadingAttachmentUuid = null;
          tab?.close();

          // Known answer: the attachment was not kept.
          if (error?.status === 404) {
            this.toastr.warning(
              this.translate.instant(
                'SENT_EMAILS.DETAIL.ATTACHMENT_NOT_AVAILABLE',
              ),
            );
            return;
          }

          if (SentEmailTools.isModuleMissingError(error)) {
            this.close();
            return;
          }

          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' SEM003',
            this.router,
          );
        },
      });
  }

  public recipientLabel(recipient: SentEmailRecipientModel): string {
    return SentEmailTools.recipientLabel(recipient);
  }

  public fileSize(attachment: SentEmailAttachmentModel): string {
    return SentEmailTools.fileSize(
      attachment?.size_bytes,
      this.translate.currentLang,
    );
  }

  public attachmentKindKey(attachment: SentEmailAttachmentModel): string {
    const known = ['REPORT', 'USER_FILE', 'ICS'];
    return known.includes(attachment?.kind)
      ? 'SENT_EMAILS.DETAIL.ATTACHMENT_KIND_' + attachment.kind
      : 'SENT_EMAILS.DETAIL.ATTACHMENT_KIND_USER_FILE';
  }

  /** Why the download button is disabled. */
  public notKeptKey(attachment: SentEmailAttachmentModel): string {
    const known = ['PENDING', 'NOT_KEPT', 'MISSING', 'FAILED'];
    return known.includes(attachment?.storage_status)
      ? 'SENT_EMAILS.DETAIL.NOT_KEPT_' + attachment.storage_status
      : 'SENT_EMAILS.DETAIL.NOT_KEPT_DEFAULT';
  }

  public isProblemEvent(event: SentEmailEventModel): boolean {
    return SentEmailDetailComponent.PROBLEM_EVENTS.includes(event?.event);
  }

  public isSuccessEvent(event: SentEmailEventModel): boolean {
    return SentEmailDetailComponent.SUCCESS_EVENTS.includes(event?.event);
  }

  public get fromLabel(): string {
    if (!this.email) {
      return '';
    }
    if (this.email.from_name && this.email.from_email) {
      return this.email.from_name + ' <' + this.email.from_email + '>';
    }
    return this.email.from_email || this.email.from_name || '';
  }

  public get replyTo(): string[] {
    return (this.email?.reply_to ?? []).filter((address) => !!address);
  }

  private buildRecipientGroups(): void {
    const recipients = this.email?.recipients ?? [];
    const groups: RecipientGroup[] = [
      { kind: 'TO', labelKey: 'SENT_EMAILS.DETAIL.KIND_TO', recipients: [] },
      { kind: 'CC', labelKey: 'SENT_EMAILS.DETAIL.KIND_CC', recipients: [] },
      { kind: 'BCC', labelKey: 'SENT_EMAILS.DETAIL.KIND_BCC', recipients: [] },
    ];
    recipients.forEach((recipient) => {
      const group = groups.find((item) => item.kind === recipient.kind);
      (group ?? groups[0]).recipients.push(recipient);
    });
    this.recipientGroups = groups.filter(
      (group) => group.recipients.length > 0,
    );
  }

  private buildEvents(): void {
    this.events = [...(this.detail?.events ?? [])].sort((a, b) => {
      const timeA = a.occurred_at ? new Date(a.occurred_at).getTime() : 0;
      const timeB = b.occurred_at ? new Date(b.occurred_at).getTime() : 0;
      return timeA - timeB;
    });
  }

  private buildContent(): void {
    this.frameDocument = null;
    this.textBody = null;

    if (!this.detail?.has_content) {
      return;
    }

    const html = (this.detail.html_body ?? '').trim();
    if (html !== '') {
      // Trusted ONLY as the srcdoc of the sandboxed iframe (no script, opaque
      // origin): Angular's sanitizer would strip the <style> blocks and inline
      // styles the email layout relies on.
      this.frameDocument = this.sanitizer.bypassSecurityTrustHtml(
        this.buildFrameDocument(html),
      );
      return;
    }

    const text = (this.detail.text_body ?? '').trim();
    this.textBody = text !== '' ? text : null;
  }

  private buildFrameDocument(html: string): string {
    const head = SentEmailDetailComponent.FRAME_HEAD;

    if (/<head(\s[^>]*)?>/i.test(html)) {
      return html.replace(/<head(\s[^>]*)?>/i, (match) => match + head);
    }
    if (/<html(\s[^>]*)?>/i.test(html)) {
      return html.replace(
        /<html(\s[^>]*)?>/i,
        (match) => match + '<head>' + head + '</head>',
      );
    }
    return (
      '<!doctype html><html><head><meta charset="utf-8">' +
      head +
      '</head><body>' +
      html +
      '</body></html>'
    );
  }

  private showBlockedTabToast(url: string): void {
    const safeUrl = url
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Inline style is stripped by the sanitizer: `.gt3-toast-link` (styles.scss).
    const message =
      this.translate.instant('SENT_EMAILS.DETAIL.POPUP_BLOCKED') +
      '<br><a class="gt3-toast-link" href="' +
      safeUrl +
      '" target="_blank" rel="noopener noreferrer">' +
      this.translate.instant('SENT_EMAILS.DETAIL.POPUP_OPEN_LINK') +
      '</a>';
    this.toastr.warning(message, '', {
      enableHtml: true,
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: false,
    });
  }
}
